/**
 * IA Sticky Card: <ia-sticky-card>
 * --------------------------------------------------------------------
 * Slides the fixed bottom buy card in and out. The card is visible only when
 * BOTH hold:
 *   1. the section's in-flow sentinel has entered the viewport or sits above
 *      it, so the card never shows while the page is still above the section's
 *      own position in the template. Measured from the sentinel rect on every
 *      scroll/resize frame, not from intersection changes: an instant jump such
 *      as "back to top" skips the 1px sentinel entirely and would leave an
 *      observer-based flag stale;
 *   2. none of the "blockers" is on screen: another section's CTA button
 *      (.ia-atc__button), so a section that has its own button never gets a
 *      second one on top, and the footer, so the card never covers it.
 * In the theme editor the card is also forced visible while its section is
 * selected, so the merchant can style it without scrolling.
 *
 * Markup (rendered by sections/ia-sticky-card.liquid):
 *   <ia-sticky-card data-section-id="…">
 *     <div data-ia-sc-sentinel></div>
 *     <div class="ia-sc__bar" data-ia-sc-bar>…</div>
 *   </ia-sticky-card>
 */
if (!customElements.get('ia-sticky-card')) {
  class IaStickyCard extends HTMLElement {
    static get BLOCKER_SELECTOR() {
      return '.ia-atc__button, footer.footer, .shopify-section-group-footer-group';
    }

    connectedCallback() {
      this.bar = this.querySelector('[data-ia-sc-bar]');
      this.sentinel = this.querySelector('[data-ia-sc-sentinel]');
      if (!this.bar || !this.sentinel || !('IntersectionObserver' in window)) return;

      this.forceVisible = false;
      this.visibleBlockers = new Set();
      this.frame = 0;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);

      this.blockerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.visibleBlockers.add(entry.target);
            } else {
              this.visibleBlockers.delete(entry.target);
            }
          });
          this.update();
        },
        { threshold: 0 }
      );
      this.observeBlockers();

      // Sections added or removed in the theme editor change the blocker set.
      this.onSectionChange = () => this.observeBlockers();
      document.addEventListener('shopify:section:load', this.onSectionChange);
      document.addEventListener('shopify:section:unload', this.onSectionChange);

      if (window.Shopify && window.Shopify.designMode) {
        this.onSelect = (event) => this.setForced(event, true);
        this.onDeselect = (event) => this.setForced(event, false);
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
      }
    }

    disconnectedCallback() {
      if (this.blockerObserver) this.blockerObserver.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
      }
      if (this.onSectionChange) {
        document.removeEventListener('shopify:section:load', this.onSectionChange);
        document.removeEventListener('shopify:section:unload', this.onSectionChange);
      }
      if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
      if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
    }

    /* Re-collect every blocker on the page (CTA buttons except the card's own, footer). */
    observeBlockers() {
      this.blockerObserver.disconnect();
      this.visibleBlockers.clear();
      document.querySelectorAll(IaStickyCard.BLOCKER_SELECTOR).forEach((el) => {
        if (this.contains(el)) return;
        this.blockerObserver.observe(el);
      });
      this.update();
    }

    /* Theme editor: while the section is selected, the section wrapper itself becomes the
       fixed bottom panel (see .ia-section-sticky-card.is-editor-selected in the CSS) so the
       editor's outline lands on the card instead of the in-flow notice line. */
    setForced(event, forced) {
      if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
      this.forceVisible = forced;
      const wrapper = this.closest('.shopify-section');
      if (wrapper) wrapper.classList.toggle('is-editor-selected', forced);
      this.update();
    }

    update() {
      // Reached = the sentinel's top edge is inside the viewport or above it: content below
      // the section's position is on screen. A CTA still visible above the line is caught
      // by the blocker rule, so the card never doubles a button that is on screen.
      const reachedSentinel = this.sentinel.getBoundingClientRect().top < window.innerHeight;
      const visible = this.forceVisible || (reachedSentinel && this.visibleBlockers.size === 0);
      this.bar.classList.toggle('ia-sc__bar--visible', visible);
    }
  }

  customElements.define('ia-sticky-card', IaStickyCard);
}
